import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Mic, MicOff, Video as VideoIcon, VideoOff, MonitorUp, PhoneOff, Loader2, Users, Sparkles } from "lucide-react";
import { VirtualBg, VIDEO_BG_URL } from "@/lib/videoBg";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());

export default function VideoCall() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const isHost = sp.get("host") === "1";

  const [meeting, setMeeting] = useState(null);
  const [phase, setPhase] = useState("lobby"); // lobby | connecting | live | ended | error
  const [name, setName] = useState(sp.get("name") || localStorage.getItem("fl_call_name") || "");
  const [err, setErr] = useState("");
  const [remoteName, setRemoteName] = useState("");
  const [connected, setConnected] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [bgType, setBgType] = useState(isHost ? "image" : "none");
  const bgTypeRef = useRef(isHost ? "image" : "none");
  const lobbyVideo = useRef(null);
  const BG_OPTS = isHost ? ["none", "blur", "image"] : ["none", "blur"];
  const BG_LABEL = { none: "No background", blur: "Blur", image: "Fieldlink" };

  const peerId = useRef(uid());
  const pcRef = useRef(null);
  const localStream = useRef(null);   // stream whose tracks are sent to the peer
  const rawStream = useRef(null);     // raw camera+mic from getUserMedia
  const compositor = useRef(null);    // VirtualBg instance (host only)
  const cameraTrack = useRef(null);
  const remotePeer = useRef(null);
  const iceServers = useRef([]);
  const sinceRef = useRef(0);
  const pollTimer = useRef(null);
  const pollBusy = useRef(false);
  const localVideo = useRef(null);
  const remoteVideo = useRef(null);
  const startedOffer = useRef(false);
  const pendingCands = useRef([]);

  const flushCands = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    const q = pendingCands.current; pendingCands.current = [];
    for (const cand of q) { try { await pc.addIceCandidate(cand); } catch { /* ignore */ } }
  }, []);

  useEffect(() => {
    let ok = true;
    api.get(`/video/public/${token}`)
      .then((r) => { if (ok) { setMeeting(r.data); iceServers.current = r.data.ice_servers || []; } })
      .catch(() => { if (ok) { setErr("This meeting link is not available."); setPhase("error"); } });
    return () => { ok = false; };
  }, [token]);

  const signal = useCallback((to, kind, payload) => {
    api.post(`/video/rooms/${token}/signal`, { from_peer: peerId.current, to_peer: to, kind, payload }).catch(() => {});
  }, [token]);

  const ensurePc = useCallback(() => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({ iceServers: iceServers.current });
    pc.onicecandidate = (e) => { if (e.candidate && remotePeer.current) signal(remotePeer.current, "candidate", e.candidate.toJSON()); };
    pc.ontrack = (e) => { if (remoteVideo.current) remoteVideo.current.srcObject = e.streams[0]; setConnected(true); };
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState)) setConnected(false);
      if (pc.connectionState === "connected") setConnected(true);
    };
    pc.oniceconnectionstatechange = () => {
      if (["connected", "completed"].includes(pc.iceConnectionState)) setConnected(true);
      if (pc.iceConnectionState === "failed") { try { pc.restartIce(); } catch { /* noop */ } }
    };
    localStream.current?.getTracks().forEach((t) => pc.addTrack(t, localStream.current));
    pcRef.current = pc;
    return pc;
  }, [signal]);

  const makeOffer = useCallback(async (to) => {
    if (startedOffer.current) return;
    startedOffer.current = true;
    remotePeer.current = to;
    const pc = ensurePc();
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    signal(to, "offer", { sdp: pc.localDescription.sdp });
  }, [ensurePc, signal]);

  const handleMessage = useCallback(async (m) => {
    if (m.kind === "offer") {
      remotePeer.current = m.from_peer;
      const pc = ensurePc();
      await pc.setRemoteDescription({ type: "offer", sdp: m.payload.sdp });
      await flushCands();
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      signal(m.from_peer, "answer", { sdp: pc.localDescription.sdp });
    } else if (m.kind === "answer") {
      const pc = ensurePc();
      if (!pc.currentRemoteDescription) { await pc.setRemoteDescription({ type: "answer", sdp: m.payload.sdp }); await flushCands(); }
    } else if (m.kind === "candidate") {
      const pc = ensurePc();
      if (pc.remoteDescription && pc.remoteDescription.type) { try { await pc.addIceCandidate(m.payload); } catch { /* ignore */ } }
      else pendingCands.current.push(m.payload);
    } else if (m.kind === "bye") {
      setConnected(false); setRemoteName("");
      if (remoteVideo.current) remoteVideo.current.srcObject = null;
      remotePeer.current = null; startedOffer.current = false; pendingCands.current = [];
      if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    }
  }, [ensurePc, signal, flushCands]);

  const poll = useCallback(async () => {
    if (pollBusy.current) return;
    pollBusy.current = true;
    try {
      const r = await api.get(`/video/rooms/${token}/poll`, { params: { peer_id: peerId.current, since: sinceRef.current } });
      sinceRef.current = r.data.next;
      const peers = r.data.peers || [];
      if (peers.length) setRemoteName(peers[0].name); else setRemoteName("");
      if (!pcRef.current && !startedOffer.current && peers.length && peerId.current > peers[0].peer_id) {
        makeOffer(peers[0].peer_id);
      }
      for (const m of r.data.messages) await handleMessage(m); // eslint-disable-line no-await-in-loop
    } catch { /* transient */ }
    finally { pollBusy.current = false; }
  }, [token, handleMessage, makeOffer]);

  // Start the camera + compositor as soon as we're in the lobby so the user
  // gets a live self-preview and can pick a background before joining.
  useEffect(() => {
    if (!meeting || phase !== "lobby" || rawStream.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rs = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: true });
        if (cancelled) { rs.getTracks().forEach((t) => t.stop()); return; }
        rawStream.current = rs;
        cameraTrack.current = rs.getVideoTracks()[0];
        try {
          const comp = new VirtualBg({ width: 1280, height: 720, bgType: bgTypeRef.current });
          const cs = await comp.init(rs, VIDEO_BG_URL);
          if (cancelled) { comp.stop(); rs.getTracks().forEach((t) => t.stop()); return; }
          compositor.current = comp;
          localStream.current = new MediaStream([cs.getVideoTracks()[0], ...rs.getAudioTracks()]);
          if (lobbyVideo.current) lobbyVideo.current.srcObject = localStream.current;
        } catch {
          compositor.current = null;
          localStream.current = rs;
          if (lobbyVideo.current) lobbyVideo.current.srcObject = rs;
        }
      } catch {
        setErr("We couldn't access your camera or microphone. Please allow access and try again.");
      }
    })();
    return () => { cancelled = true; };
  }, [meeting, phase]);

  // Mirror the outgoing stream into the live-view local preview.
  useEffect(() => {
    if (phase === "live" && localVideo.current && localStream.current) localVideo.current.srcObject = localStream.current;
  }, [phase]);

  const changeBg = (t) => {
    bgTypeRef.current = t;
    setBgType(t);
    compositor.current?.setBgType(t);
  };
  const cycleBg = () => {
    const i = BG_OPTS.indexOf(bgType);
    changeBg(BG_OPTS[(i + 1) % BG_OPTS.length]);
  };

  const join = async () => {
    setErr("");
    if (!name.trim()) { setErr("Please enter your name to join."); return; }
    localStorage.setItem("fl_call_name", name.trim());
    if (!localStream.current) { setErr("Camera isn't ready yet — please allow access and wait a moment."); return; }
    setPhase("connecting");
    try {
      const r = await api.post(`/video/rooms/${token}/join`, { peer_id: peerId.current, name: name.trim(), role: isHost ? "host" : "guest" });
      iceServers.current = r.data.ice_servers || iceServers.current;
      const peers = r.data.peers || [];
      if (peers.length) setRemoteName(peers[0].name);
      setPhase("live");
      sinceRef.current = 0;
      pollTimer.current = setInterval(poll, 900);
      poll();
    } catch {
      setPhase("lobby");
      setErr("Couldn't join the call — please try again.");
    }
  };

  const toggleMic = () => {
    const t = rawStream.current?.getAudioTracks()[0];
    if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); }
  };
  const toggleCam = () => {
    const t = cameraTrack.current;
    if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); }
  };

  const videoSender = () => pcRef.current?.getSenders().find((s) => s.track && s.track.kind === "video");

  const shareScreen = async () => {
    // With the compositor, the screen fills the frame and the person drops into
    // the bottom-right corner — no track swap or renegotiation needed.
    if (compositor.current) {
      if (sharing) { compositor.current.setScreen(null); setSharing(false); return; }
      try {
        const ds = await navigator.mediaDevices.getDisplayMedia({ video: true });
        compositor.current.setScreen(ds);
        ds.getVideoTracks()[0].onended = () => { compositor.current?.setScreen(null); setSharing(false); };
        setSharing(true);
      } catch { /* cancelled */ }
      return;
    }
    // Fallback (no compositor): replace the outgoing video track.
    const sender = videoSender();
    if (sharing) {
      if (sender && cameraTrack.current) await sender.replaceTrack(cameraTrack.current);
      if (localVideo.current) localVideo.current.srcObject = localStream.current;
      setSharing(false);
      return;
    }
    try {
      const ds = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = ds.getVideoTracks()[0];
      if (sender) await sender.replaceTrack(screenTrack);
      if (localVideo.current) localVideo.current.srcObject = ds;
      screenTrack.onended = async () => {
        const s = videoSender();
        if (s && cameraTrack.current) await s.replaceTrack(cameraTrack.current);
        if (localVideo.current) localVideo.current.srcObject = localStream.current;
        setSharing(false);
      };
      setSharing(true);
    } catch { /* user cancelled */ }
  };

  const teardown = useCallback(() => {
    clearInterval(pollTimer.current);
    api.post(`/video/rooms/${token}/leave`, { peer_id: peerId.current, name: name || "Guest", role: isHost ? "host" : "guest" }).catch(() => {});
    compositor.current?.stop(); compositor.current = null;
    rawStream.current?.getTracks().forEach((t) => t.stop());
    localStream.current?.getTracks().forEach((t) => t.stop());
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
  }, [token, name, isHost]);

  const hangUp = () => { teardown(); setPhase("ended"); };

  useEffect(() => () => teardown(), [teardown]);

  // ---------- Render ----------
  if (phase === "error") {
    return <Shell><div className="text-center"><div className="text-2xl font-black">Link unavailable</div><p className="text-white/60 mt-2">{err || "This meeting link is not available."}</p></div></Shell>;
  }
  if (phase === "ended") {
    return <Shell><div className="text-center"><div className="text-3xl font-black">Call ended</div><p className="text-white/60 mt-2">Thanks for joining. You can close this tab.</p></div></Shell>;
  }
  if (phase === "lobby" || phase === "connecting") {
    return (
      <Shell>
        <div className="w-full max-w-lg rounded-2xl border border-white/12 bg-[rgba(9,17,25,0.92)] backdrop-blur-xl p-7 sm:p-8" data-testid="video-lobby">
          <div className="text-[12px] font-mono font-bold uppercase tracking-[0.3em] text-[#FF5F15]">// Video demo</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl mt-2">{meeting?.title || "Fieldlink demo"}</h1>
          {meeting?.host_name && <p className="text-white/60 mt-1 text-sm">Hosted by <span className="text-white font-semibold">{meeting.host_name}</span></p>}

          <div className="mt-5 relative rounded-xl overflow-hidden bg-black aspect-video border border-white/10">
            <video ref={lobbyVideo} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="video-lobby-preview" />
            {!camOn && <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm">Camera off</div>}
          </div>

          <div className="mt-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/50 mb-2">Background</div>
            <div className="flex gap-2" data-testid="video-bg-chooser">
              {BG_OPTS.map((opt) => (
                <button key={opt} type="button" onClick={() => changeBg(opt)} data-testid={`video-bg-${opt}`}
                  className={`flex-1 h-10 rounded-lg text-sm font-semibold border transition-colors ${bgType === opt ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-white/15 text-white/80 hover:bg-white/5"}`}>
                  {BG_LABEL[opt]}
                </button>
              ))}
            </div>
          </div>

          <label className="block text-[11px] font-bold uppercase tracking-[0.22em] text-white/55 mt-5 mb-2">Your name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane from ABC Civil"
            className="w-full h-[54px] rounded-md bg-[rgba(7,18,28,0.8)] border border-white/15 px-4 text-white outline-none focus:border-[#FF5F15]"
            data-testid="video-name-input" onKeyDown={(e) => e.key === "Enter" && join()} />
          {err && <p className="text-red-400 text-sm mt-3" data-testid="video-lobby-error">{err}</p>}
          <button onClick={join} disabled={phase === "connecting"} data-testid="video-join-button"
            className="mt-5 w-full h-[56px] rounded-md bg-[#FF5F15] hover:bg-[#ff6f2e] disabled:opacity-70 text-white font-black uppercase tracking-[0.12em] flex items-center justify-center gap-2 transition-colors">
            {phase === "connecting" ? <><Loader2 className="w-5 h-5 animate-spin" /> Connecting…</> : "Join call"}
          </button>
          <p className="text-white/40 text-xs mt-4 text-center">Opens right in your browser — no download or account needed.</p>
        </div>
      </Shell>
    );
  }

  // live
  return (
    <div className="fixed inset-0 bg-[#05090F] text-white flex flex-col" data-testid="video-live">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-[#FF5F15] animate-pulse" />
          <span className="font-display font-black truncate">{meeting?.title || "Fieldlink demo"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-white/60">
          <Users className="w-4 h-4" /> {connected ? "2 connected" : "Waiting for the other person…"}
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden bg-black">
        <video ref={remoteVideo} autoPlay playsInline className="absolute inset-0 w-full h-full object-contain bg-black" data-testid="video-remote" />
        {!connected && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 gap-3">
            <Loader2 className="w-8 h-8 animate-spin" />
            <div className="text-sm">{remoteName ? `Connecting to ${remoteName}…` : "Waiting for the other person to join…"}</div>
          </div>
        )}
        {connected && remoteName && (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-black/50 text-xs font-semibold">{remoteName}</div>
        )}
        {/* local PIP */}
        <div className="absolute bottom-4 right-4 w-40 sm:w-56 aspect-video rounded-lg overflow-hidden border border-white/20 shadow-lg bg-[#0B1520]">
          <video ref={localVideo} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="video-local" />
          {!camOn && !sharing && <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs">Camera off</div>}
          <div className="absolute bottom-1 left-1.5 text-[10px] font-semibold bg-black/50 px-1.5 rounded">You{sharing ? " · sharing" : ""}</div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 py-4 border-t border-white/10">
        <Ctrl on={micOn} onClick={toggleMic} OnIcon={Mic} OffIcon={MicOff} label="Mic" testid="video-mic" />
        <Ctrl on={camOn} onClick={toggleCam} OnIcon={VideoIcon} OffIcon={VideoOff} label="Camera" testid="video-cam" />
        <button onClick={cycleBg} data-testid="video-bg-toggle" title="Change background"
          className={`h-12 px-4 rounded-full flex items-center gap-2 text-sm font-semibold transition-colors ${bgType !== "none" ? "bg-[#FF5F15] text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}>
          <Sparkles className="w-5 h-5" /> {BG_LABEL[bgType]}
        </button>
        <button onClick={shareScreen} disabled={!connected} data-testid="video-share"
          className={`h-12 px-4 rounded-full flex items-center gap-2 text-sm font-semibold transition-colors disabled:opacity-40 ${sharing ? "bg-[#FF5F15] text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}>
          <MonitorUp className="w-5 h-5" /> {sharing ? "Stop sharing" : "Share screen"}
        </button>
        <button onClick={hangUp} data-testid="video-hangup"
          className="h-12 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center gap-2 text-sm font-bold transition-colors">
          <PhoneOff className="w-5 h-5" /> Leave
        </button>
      </div>
    </div>
  );
}

function Ctrl({ on, onClick, OnIcon, OffIcon, label, testid }) {
  const Icon = on ? OnIcon : OffIcon;
  return (
    <button onClick={onClick} data-testid={testid} aria-label={label}
      className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors ${on ? "bg-white/10 hover:bg-white/20 text-white" : "bg-red-600/80 hover:bg-red-600 text-white"}`}>
      <Icon className="w-5 h-5" />
    </button>
  );
}

function Shell({ children }) {
  return (
    <div className="fixed inset-0 bg-[#05090F] text-white flex items-center justify-center p-4"
      style={{ backgroundImage: "linear-gradient(rgba(5,9,15,0.86),rgba(5,9,15,0.92)), url(/hero-bg.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}>
      {children}
    </div>
  );
}
